import React, { createContext, useContext, useState, useEffect } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");

        console.log("Auth loadUser", {
          hasToken: !!token,
          hasUser: !!userData,
        });

        if (
          token &&
          userData &&
          userData !== "undefined" &&
          userData !== "null"
        ) {
          try {
            const parsedUser = JSON.parse(userData);

            // ✅ FIXED VALIDATION
            if (!parsedUser.role || !(parsedUser.id || parsedUser._id)) {
              console.error("❌ User data missing required fields:", {
                hasRole: !!parsedUser.role,
                hasId: !!(parsedUser.id || parsedUser._id),
              });
              setError("Invalid user data - missing required fields");
              setUser(null);
              return;
            }

            console.log("User authenticated", parsedUser.role);
            setUser(parsedUser);
            setError(null);

          } catch (parseError) {
            console.error("❌ Error parsing user data:", parseError);
            setError("Failed to parse user data");
          }

        } else {
          console.log("⚠️ No valid token or user data found");
          setUser(null);
        }

      } catch (err) {
        console.error("❌ AuthContext loadUser error:", err);
        setError(err.message);

        if (err.message.includes("critical")) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }

      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (userData, token) => {
    try {
      setError(null);

      // ✅ STORE TOKEN + USER
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));

      const essentialData = {
        role: userData.role,
        userId: userData.id || userData._id,
        companyId: userData.organizationId || userData.companyId,
        isActive: userData.isActive,
        isVerified: userData.isVerified,
      };

      Object.entries(essentialData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          localStorage.setItem(key, value.toString());
        }
      });

      if (userData.role === "admin") {
        const companyId = userData.organizationId || userData.companyId;

        if (companyId) {
          console.log("✅ Admin user logged in with companyId:", companyId);
        } else {
          console.error("❌ CRITICAL: Admin user without companyId");
          setError("Company association missing. Contact support.");
        }
      }

      console.log("✅ Login stored:", {
        role: userData.role,
        companyId: essentialData.companyId,
      });

      setUser(userData);
      return userData;

    } catch (err) {
      console.error("❌ Login error:", err);
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    const keysToRemove = [
      "token",
      "user",
      "companyId",
      "role",
      "userId",
      "isActive",
      "isVerified",
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    console.log("🚪 Logged out");
    setUser(null);
    setError(null);
  };

  const validateDataFlow = () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    const role = localStorage.getItem("role");
    const companyId = localStorage.getItem("companyId");

    const validation = {
      hasToken: !!token,
      hasUser: !!user,
      hasRole: !!role,
      hasCompanyId: !!companyId,
      isComplete: false,
    };

    if (role === "admin") {
      validation.isComplete =
        validation.hasToken &&
        validation.hasUser &&
        validation.hasRole &&
        validation.hasCompanyId;
    } else {
      validation.isComplete =
        validation.hasToken &&
        validation.hasUser &&
        validation.hasRole;
    }

    console.log("🔍 Data flow validation:", validation);
    return validation;
  };

  const value = {
    user,
    token: localStorage.getItem("token"),
    login,
    logout,
    loading,
    error,
    validateDataFlow,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
